<script lang="ts" setup>
import type { MaybeRef } from 'vue'
import { formatSeconds } from 'core'

import AudioPlayerPeaks from './AudioPlayerPeaks.vue'
import { useAudioPlayback } from './utils'

const props = defineProps<{
  url: string
  peaks: number[]
  audioRef?: MaybeRef<HTMLAudioElement>
}>()

const { peaksRef, state, togglePlay } = useAudioPlayback(props.url, props)
</script>

<template>
  <div :class="[$style.wrapper, state.hasError && $style.error]">
    <ToolTip
      v-if="state.hasError"
      text="We could not load the audio file. Please make sure your network connection is stable. If this issue persists, please contact us."
    >
      <button class="!size-$player-button-size,36px p-2 text-red-5 text-base bg-red-5/10 rounded-full flex-shrink-0">
        <i-solar-volume-cross-bold-duotone />
      </button>
    </ToolTip>

    <CTAButton v-else :class="[$style.cta, state.isLoading && $style.loading]" class="!size-$player-button-size,36px !p-0 !rounded-full !overflow-visible relative flex-shrink-0 text-xs" :inert="state.isLoading" @click="togglePlay">
      <i-solar-pause-bold v-if="state.isPlaying" />
      <i-solar-play-bold v-else />
    </CTAButton>

    <div v-once ref="peaksRef" class="flex-grow">
      <AudioPlayerPeaks :peaks />
    </div>

    <span class="w-32px font-medium text-xs text-center opacity-70">
      {{ formatSeconds((state.isPlaying || state.time > 0) ? state.time : state.duration) }}
    </span>

    <slot />
  </div>
</template>

<style module lang="sass">
.wrapper
  @apply gap-2 flex-center

.cta
  color: var(--player-cta-icon, theme('colors.primary.50')) !important
  background-color: var(--player-cta-bg, theme('colors.primary.500')) !important

  // Loader
  &:before
    @apply content-[''] absolute -inset-5px opacity-0
    @apply rounded-full border-2px border-dashed border-$player-cta-bg animate-spin animate-duration-2s animate-ease-in-out

    transition: opacity 200ms

  &.loading
    @apply before:opacity-40 $cta-outline-transparent
//;
</style>
